import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import RouteFallback from "./RouteFallback";
import { ExperienceProvider, useExperience } from "../features/experience";
import { AdaptiveSidebar } from "./AdaptiveSidebar";
import { InternalBanners } from "./SiteBanners";
import { toast } from "sonner";
import { LogOut, Plus, Menu, Smartphone, Sparkles, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../lib/siteContent";
// Lazy: the Add wizard pulls in the document scanner stack (jsPDF, QR) which previously sat in
// the entry bundle for every visitor. It now loads on first open only.
const AddResponsibilityWizard = lazy(() => import("./AddResponsibilityWizard"));
// Lazy + viewport-gated: desktop users never download the mobile tab shell chunk.
const TabShell = lazy(() => import("./mobile/TabShell"));
import UpgradeModal from "./UpgradeModal";
import ThemeSwitcher from "./ThemeSwitcher";
import SubscriptionWidget, { useSubscription } from "./SubscriptionWidget";
import LegalUpdateBanner from "./LegalUpdateBanner";
import SubscriptionEndingBanner from "./SubscriptionEndingBanner";
import PastDueBanner from "./PastDueBanner";
import { prefetchLikelyRoutes } from "../lib/prefetchRoutes";
import OnboardingWizard from "./OnboardingWizard";
import AdaptiveOnboarding from "./AdaptiveOnboarding";
import PlanniTip from "./planni/PlanniTip";
import { BabyNameExplorerLink } from "./BabyNameExplorerLink";
import SeasonalAmbient from "./theme/SeasonalAmbient";
import SeasonalDecor from "./theme/SeasonalDecor";
import SeasonalFrame from "./theme/SeasonalFrame";
import SeasonalSuggestion from "./theme/SeasonalSuggestion";
import { useAppearance } from "../context/AppearanceContext";
import { useIsMobile } from "../hooks/useViewport";
import PostRemovalPrompt from "./PostRemovalPrompt";
import GlobalSearch from "./GlobalSearch";
import WhatsNew from "./WhatsNew";
import WelcomeToPlannio from "./WelcomeToPlannio";
import StorageIndicator from "./StorageIndicator";


const PLAN_BADGE = {
  free: { label: "Free", cls: "bg-[#f0f1ee] text-[#636E72]" },
  premium: { label: "Premium", cls: "bg-[#0F766E]/10 text-[#0F766E]" },
  family: { label: "Family", cls: "bg-[#7C3AED]/10 text-[#7C3AED]" },
};

// The Experience provider mounts the adaptive contract for every authenticated page.
export default function AppLayout() {
  return (
    <ExperienceProvider>
      <AppLayoutInner />
    </ExperienceProvider>
  );
}

function AppLayoutInner() {
  const { experience } = useExperience();
  const { user, logout, refresh } = useAuth();
  const sc = useSiteContent();
  const [tourDone, setTourDone] = useState(false);
  const { hydrateFromAccount } = useAppearance();
  useEffect(() => { hydrateFromAccount(user?.settings?.appearance); }, [user?.settings?.appearance, hydrateFromAccount]);
  useEffect(() => { prefetchLikelyRoutes(); }, []);
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();
  const [upgrade, setUpgrade] = useState(false);
  const [upgradeCfg, setUpgradeCfg] = useState({});
  const [unread, setUnread] = useState(0);
  const [connectUnread, setConnectUnread] = useState(false);
  const [supportAttention, setSupportAttention] = useState(0);
  const sub = useSubscription();
  const govPlan = sub?.governing_plan || user?.plan || "free";
  const isPremium = ["premium", "family"].includes(govPlan);
  const planBadge = PLAN_BADGE[govPlan] || PLAN_BADGE.free;

  const refreshUnread = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
  }, []);
  const refreshConnectUnread = useCallback(() => {
    if (!isPremium) { setConnectUnread(false); return; }
    api.get("/connect/unread-summary").then((r) => setConnectUnread(!!r.data.has_unread)).catch(() => {});
  }, [isPremium]);
  const refreshSupport = useCallback(() => {
    api.get("/support-hub/attention").then((r) => setSupportAttention(r.data.count || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    refreshUnread();
    refreshConnectUnread();
    refreshSupport();
    window.addEventListener("notif-updated", refreshUnread);
    window.addEventListener("connect-updated", refreshConnectUnread);
    window.addEventListener("support-updated", refreshSupport);
    return () => { window.removeEventListener("notif-updated", refreshUnread); window.removeEventListener("connect-updated", refreshConnectUnread); window.removeEventListener("support-updated", refreshSupport); };
  }, [refreshUnread, refreshConnectUnread, refreshSupport]);

  // Redeem a gift captured at sign-up or from a gift email, once authenticated for the first time.
  useEffect(() => {
    if (!user) return;
    const raw = (localStorage.getItem("plannio_pending_gift") || "").trim();
    if (!raw) return;
    localStorage.removeItem("plannio_pending_gift");
    let body;
    try {
      const p = JSON.parse(raw);
      body = { token: p.token || null, code: p.code || null };
    } catch {
      body = { code: raw, token: null };
    }
    if (!body.token && !body.code) return;
    api.post("/gifts/redeem", body)
      .then(({ data }) => {
        toast.success(data.queued
          ? `Your gifted ${data.plan} membership will begin when your current plan ends.`
          : `Your gifted ${data.plan} membership is now active. Enjoy PlannioLife!`);
        if (refresh) refresh();
      })
      .catch((e) => {
        const d = e.response?.data?.detail;
        toast.info(typeof d === "string" ? d : "We could not apply your gift automatically. You can redeem it from the gift page.");
      });
  }, [user, refresh]);

  const openHubUpgrade = (title, message) => {
    setMobileOpen(false);
    setUpgradeCfg({ variant: "premium", title, message, includedIn: ["Premium", "Family"] });
    setUpgrade(true);
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col gap-1 overflow-y-auto p-5">
      <button type="button" onClick={() => navigate("/app/dashboard")} data-testid="sidebar-logo-home"
        className="flex items-center gap-2.5 px-2 pb-6 text-left transition-opacity hover:opacity-80">
        <img src={sc.brand_logo_mark || "/logo-mark.png"} alt="PlannioLife" className="theme-light-only h-9 w-9 object-contain" />
        <img src={sc.brand_logo_mark_dark || "/logo-mark-white.png"} alt="PlannioLife" className="theme-dark-only h-9 w-9 object-contain" />
        <span className="font-display text-xl font-semibold text-[#2D3436]">PlannioLife</span>
      </button>

      <button
        data-testid="add-responsibility-btn"
        onClick={() => { setWizardOpen(true); setMobileOpen(false); }}
        className="mb-4 flex items-center justify-center gap-2 rounded-xl bg-[#0F766E] px-4 py-2.5 font-medium text-white transition-transform hover:-translate-y-0.5 hover:shadow-lg"
      >
        <Plus className="h-4 w-4" strokeWidth={2} /> Quick Add
      </button>

      <GlobalSearch />

      {!experience ? (
        <div className="space-y-2 px-1" data-testid="nav-loading">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-[#F0F2EE]" />)}
        </div>
      ) : (
        <AdaptiveSidebar user={user} unread={unread} connectUnread={connectUnread} supportAttention={supportAttention} onNav={() => setMobileOpen(false)} />
      )}

      <div className="mt-auto space-y-4">
        <WhatsNew />
        {!isPremium && (
          <button
            data-testid="premium-promo-card"
            onClick={() => { setMobileOpen(false); navigate("/app/billing?plan=premium"); }}
            className="group w-full overflow-hidden rounded-2xl border border-[#0F766E]/25 bg-gradient-to-br from-[#F0FDF9] to-[#E6F7F2]/60 p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="mb-1.5 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-[#0F766E]" strokeWidth={1.8} />
              <span className="text-sm font-semibold text-[#0F766E]">More with Premium</span>
              <ArrowUpRight className="ml-auto h-4 w-4 text-[#0F766E] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            <p className="text-xs leading-relaxed text-[#0F766E]/80">Every hub is yours to try. Premium adds unlimited records, richer insights and the keepsakes worth treasuring.</p>
            <span className="mt-2.5 inline-block rounded-lg bg-[#0F766E] px-3 py-1 text-xs font-semibold text-white">See what's included</span>
          </button>
        )}
        <SubscriptionWidget onUpgrade={() => { setUpgradeCfg({ variant: "premium" }); setUpgrade(true); }} />
        <div className="rounded-2xl border border-[#EBECE8] p-4 text-center">
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-[#F4F6F2]"><Smartphone className="h-5 w-5 text-[#0F766E]" strokeWidth={1.5} /></div>
          <div className="text-sm font-medium text-[#2D3436]">Get the mobile app</div>
          <div className="mt-0.5 text-xs text-[#636E72]">Manage life on the go.</div>
          <div className="mt-3 rounded-lg border border-[#EBECE8] px-3 py-1.5 text-xs font-medium text-[#9aa3a0]">Coming Soon</div>
        </div>
        <div className="border-t border-[#E2E8F0] pt-4">
          <div className="flex items-center gap-3 px-2">
            <div className="h-9 w-9 rounded-full bg-[#A7C4A0]/40 flex items-center justify-center font-medium text-[#0F766E]">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-[#2D3436]">{user?.name}</div>
              <button data-testid="sidebar-plan-badge" onClick={() => { setMobileOpen(false); if (!user?.is_minor) navigate("/app/billing"); }} className={`mt-0.5 inline-block max-w-full truncate whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${planBadge.cls}`}>{planBadge.label}</button>
            </div>
            <ThemeSwitcher align="right" position="up" />
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="rounded-lg p-2 text-[#636E72] hover:bg-[#f0f1ee] hover:text-[#EF4444]"
              title="Log out"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative isolate flex min-h-screen bg-[#FAFAF8]">
      <SeasonalFrame />
      <SeasonalAmbient />
      <SeasonalDecor />
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-[#E2E8F0] bg-white/60 backdrop-blur md:block">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="safe-pt absolute left-0 top-0 h-full w-72 bg-white shadow-xl"><SidebarContent /></aside>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <InternalBanners />
        <SubscriptionEndingBanner />
        <PastDueBanner />
        <div className="safe-pt flex items-center justify-between border-b border-[#E2E8F0] bg-white/60 px-4 py-3 md:hidden">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="rounded-lg p-2 text-[#2D3436]">
            <Menu className="h-6 w-6" />
          </button>
          <span className="font-display text-lg font-semibold">PlannioLife</span>
          <div className="flex items-center gap-2">
            <StorageIndicator />
            <ThemeSwitcher align="right" position="down" />
          </div>
        </div>
        {!user?.is_minor && (
          <div className="hidden items-center justify-end border-b border-[#E2E8F0] bg-white/40 px-6 py-2.5 md:flex">
            <StorageIndicator />
          </div>
        )}
        <div className="mx-auto max-w-6xl p-5 pb-28 md:p-8 md:pb-8 lg:p-10 lg:pb-10">
          <LegalUpdateBanner />
          <Suspense fallback={<RouteFallback />}>
            <Outlet context={{ openWizard: () => setWizardOpen(true), unread, refreshUnread }} />
          </Suspense>
          <PostRemovalPrompt />
        </div>
      </main>

      {isMobile && (
        <Suspense fallback={null}>
          <TabShell onAddResponsibility={() => setWizardOpen(true)} unread={unread} />
        </Suspense>
      )}

      {wizardOpen && (
        <Suspense fallback={null}>
          <AddResponsibilityWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
        </Suspense>
      )}
      {/* Onboarding order: product intro (WelcomeToPlannio) -> navigation tour -> hub setup */}
      <WelcomeToPlannio />
      <OnboardingWizard hold={!!experience?.v2_intro?.show_modal} onDone={() => setTourDone(true)} />
      <AdaptiveOnboarding hold={!!experience?.v2_intro?.show_modal || !tourDone} />
      <SeasonalSuggestion />
      <PlanniTip />
      <BabyNameExplorerLink />
      <UpgradeModal
        open={upgrade}
        variant={upgradeCfg.variant || "premium"}
        title={upgradeCfg.title}
        includedIn={upgradeCfg.includedIn}
        message={upgradeCfg.message || "This module is part of Premium. Upgrade to unlock Travel and future modules."}
        onClose={() => { setUpgrade(false); setUpgradeCfg({}); }}
      />
    </div>
  );
}
